/* ==========================================================================
   AURA — QUICK ADD CONTROLLER
   --------------------------------------------------------------------------
   UI → AuraQuickAdd → CartTransport → Shopify cart AJAX.

   The quick-add button (snippets/aura-quick-add.liquid) emits intent only: a
   click. This controller owns the request and the feedback — button state
   (idle → loading → added), an inline error, and refreshing every
   cart-reactive section on the page. Deliberately no modal: on a grid the
   shopper adds several items in a row and must never be interrupted.

   Cart-reactive sections, one contract:
     · the header cart icon (Dawn's #cart-icon-bubble ← cart-icon-bubble), and
     · any element carrying data-aura-cart-section="{{ section.id }}" (e.g.
       sections/aura-cart-bar.liquid).
   Their IDs ride along on the /cart/add request (bundled Section Rendering),
   so ONE round-trip returns Shopify-rendered HTML for all of them — no
   client-side money formatting, no second fetch.

   Events are delegated from document, so cards appended later by Load More
   or swapped in by a filter change work without rebinding.

   The transport is the same seam the planned AuraCartController will use:
   BaseCartTransport defines the contract, AjaxCartTransport talks to
   Shopify's cart AJAX API.
   ========================================================================== */
(function () {
  'use strict';

  const ADDED_MS = 1800;
  const GENERIC_ERROR = 'Izdelka ni bilo mogoče dodati v košarico. Poskusite znova.';

  /* -------- Transports (interchangeable; identical interface) ------------ */

  class BaseCartTransport {
    add(_items, _sectionIds) {
      throw new Error('CartTransport.add(items, sectionIds) must be implemented by a subclass.');
    }
  }

  class AjaxCartTransport extends BaseCartTransport {
    add(items, sectionIds) {
      const body = { items };
      if (sectionIds.length) {
        body.sections = sectionIds;
        /* Render the sections in THIS page's context — including the query
           string, so an alternate template reached via ?view= still resolves
           its own template sections. */
        body.sections_url = window.location.pathname + window.location.search;
      }

      return fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      }).then((res) =>
        res.json().then((data) => {
          if (!res.ok || data.status) throw data;
          return data;
        })
      );
    }
  }

  /* -------- Controller ---------------------------------------------------- */

  class AuraQuickAdd {
    constructor(transport) {
      this.transport = transport;
      document.addEventListener('click', (e) => this.onClick(e));
    }

    onClick(e) {
      const btn = e.target.closest && e.target.closest('.aura-quick-add__btn');
      const root = btn && btn.closest('[data-aura-quick-add]');
      if (!root || btn.disabled) return;
      e.preventDefault();
      this.add(root);
    }

    /* The header icon plus every [data-aura-cart-section] actually present.
       Shopify caps a bundled request at 5 sections. */
    cartSections() {
      const list = [{ id: 'cart-icon-bubble', selector: '#cart-icon-bubble' }];
      document.querySelectorAll('[data-aura-cart-section]').forEach((el) => {
        const id = el.dataset.auraCartSection;
        list.push({ id, selector: `[data-aura-cart-section="${id}"]` });
      });
      return list.filter((s) => document.querySelector(s.selector)).slice(0, 5);
    }

    add(root) {
      if (root.dataset.state === 'loading') return;

      const id = parseInt(root.dataset.variantId, 10);
      if (!id) return;
      const quantity = parseInt(root.dataset.quantity, 10) || 1;
      const sections = this.cartSections();

      window.clearTimeout(root._auraAddedTimer);
      this.setError(root, '');
      this.setState(root, 'loading');

      this.transport
        .add([{ id, quantity }], sections.map((s) => s.id))
        .then((data) => {
          this.refreshSections(sections, data.sections || {});
          this.setState(root, 'added');
          this.announce(root, 'Dodano v košarico');
          root._auraAddedTimer = window.setTimeout(() => {
            this.setState(root, 'idle');
            this.announce(root, '');
          }, ADDED_MS);

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'aura-quick-add',
              productVariantId: id,
              cartData: data,
            });
          }
        })
        .catch((err) => {
          this.setState(root, 'idle');
          /* Shopify's 422 carries a shopper-readable description, e.g. when
             every available unit is already in the cart. */
          this.setError(root, (err && (err.description || err.message)) || GENERIC_ERROR);
        });
    }

    /* Swap each section's fresh HTML into place. The persistent root element
       keeps its identity (so CSS transitions on it can run); only its
       contents and data-* state are replaced. The header icon's markup is
       the section's inner HTML, so it falls back to the wrapper. */
    refreshSections(sections, rendered) {
      sections.forEach((s) => {
        const html = rendered[s.id];
        const target = document.querySelector(s.selector);
        if (!html || !target) return;

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const source = doc.querySelector(s.selector) || doc.querySelector('.shopify-section');
        if (!source) return;

        target.innerHTML = source.innerHTML;
        if (source.matches(s.selector)) {
          Object.keys(source.dataset).forEach((key) => {
            target.dataset[key] = source.dataset[key];
          });
        }
      });
    }

    setState(root, state) {
      root.dataset.state = state;
      const btn = root.querySelector('.aura-quick-add__btn');
      if (btn) btn.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
    }

    setError(root, message) {
      const el = root.querySelector('[data-quick-add-error]');
      if (!el) return;
      el.textContent = message;
      el.hidden = !message;
    }

    announce(root, message) {
      const el = root.querySelector('[data-quick-add-status]');
      if (el) el.textContent = message;
    }
  }

  if (!window.auraQuickAdd) {
    window.auraQuickAdd = new AuraQuickAdd(new AjaxCartTransport());
  }
})();
